import { useMany } from '@refinedev/core';
import { keepPreviousData } from '@tanstack/react-query';
import type {
  PersonListOut,
  PublicationListOut,
} from '@/api/generated/schemas';
import { orderedAuthorIds } from '@/components/user_catalog/publications/publicationFacts';

export interface LineupPublicationLookups {
  publicationById: Map<number, PublicationListOut>;
  authorById: Map<number, PersonListOut>;
  /** While true a missing publication means "not fetched yet", not "gone". */
  isLoading: boolean;
}

const sortedIds = (ids: number[]) => [...new Set(ids)].sort((a, b) => a - b);

/** Publications (and their authors) behind a set of lineup rows. Same
 * resource+ids as the tables, so react-query shares their cache. */
export const useLineupPublications = (
  publicationIds: number[]
): LineupPublicationLookups => {
  // Sorted: `ids` is part of the query key, so passing them in row order
  // would refetch on every drag and blank the rows mid-reorder.
  const fetchIds = sortedIds(publicationIds);
  const { result: publicationsResult, query: publicationsQuery } =
    useMany<PublicationListOut>({
      resource: 'user_catalog.publications',
      ids: fetchIds,
      // Ids are the query key, so adding or removing a row starts a new
      // query — without this the rows blank out until it lands.
      queryOptions: {
        enabled: fetchIds.length > 0,
        placeholderData: keepPreviousData,
      },
    });
  const publicationById = new Map(
    publicationsResult?.data.map((p) => [p.id, p])
  );

  const authorIds = sortedIds(
    orderedAuthorIds(fetchIds.map((id) => publicationById.get(id)))
  );
  const { result: authorsResult, query: authorsQuery } = useMany<PersonListOut>(
    {
      resource: 'user_catalog.authors',
      ids: authorIds,
      queryOptions: {
        enabled: authorIds.length > 0,
        placeholderData: keepPreviousData,
      },
    }
  );
  const authorById = new Map(authorsResult?.data.map((a) => [a.id, a]));

  return {
    publicationById,
    authorById,
    isLoading: publicationsQuery.isFetching || authorsQuery.isFetching,
  };
};
